// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../store/AppContext.js';
import { SUPPORTED_LOCALE_CODES } from '../i18n/locales.js';
import { TopNav } from '../components/layout/TopNav.js';
import { Sidebar } from '../components/layout/Sidebar.js';
import { AccountTree } from '../components/accounts/AccountTree.js';
import { TransactionRegister } from '../components/transactions/TransactionRegister.js';
import { BalanceSheet } from '../components/reports/BalanceSheet.js';
import { IncomeStatement } from '../components/reports/IncomeStatement.js';
import { CashFlow } from '../components/reports/CashFlow.js';
import { TransactionReport } from '../components/reports/TransactionReport.js';
import { NetWorthChart } from '../components/reports/NetWorthChart.js';
import { ExpenseBreakdown } from '../components/reports/ExpenseBreakdown.js';
import { IncomeVsExpenses } from '../components/reports/IncomeVsExpenses.js';
import { BudgetReport } from '../components/reports/BudgetReport.js';
import { Settings } from '../components/settings/Settings.js';
import { CommodityManager } from '../components/settings/CommodityManager.js';
import { PriceEditor } from '../components/settings/PriceEditor.js';
import { ScheduledTransactions } from '../components/scheduled/ScheduledTransactions.js';
import { BudgetManager } from '../components/budgets/BudgetManager.js';
import { BudgetEditor } from '../components/budgets/BudgetEditor.js';
import { GlobalSearch } from '../components/search/GlobalSearch.js';
import { ImportPage } from '../components/import/ImportPage.js';
import { Portfolio } from '../components/investment/Portfolio.js';
import { CapitalGainsReport } from '../components/investment/CapitalGainsReport.js';
import { ExchangeRateManager } from '../components/settings/ExchangeRateManager.js';
import { CurrencyGainLoss } from '../components/reports/CurrencyGainLoss.js';
import { loadEncryptedSnapshot, saveEncryptedSnapshot } from '../lib/indexedDb.js';
import { fetchSyncLog, decryptBlobs } from '../lib/syncClient.js';
import { encryptEventsViaWorker, decryptSnapshotViaWorker } from '../store/useCryptoWorker.js';
import { buildEvent } from '../lib/eventLog.js';
import { useEncryptAndPush } from '../hooks/useEncryptAndPush.js';
import { usePriceSync } from '../hooks/usePriceSync.js';
import { isMobileShell } from '../components/mobile/useMobileShell.js';
import { MobileApp } from '../components/mobile/MobileApp.js';
import { FirstRunWizard } from '../components/onboarding/FirstRunWizard.js';
import { useOnlineStatus } from '../native/useNative.js';

// Vite worker import
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import CryptoWorker from '../workers/crypto.worker.ts?worker';

export function MainApp() {
  const { state, dispatch } = useApp();
  const { auth } = state;
  const { i18n } = useTranslation();

  const workerRef = useRef<Worker | null>(null);
  const [syncing, setSyncing] = useState(false);
  // Gate the first-run wizard: only decide "this user has no accounts" once the
  // initial cache load + first server sync have settled, so a returning user
  // never sees the wizard flash while their events are still being pulled.
  const [firstSyncDone, setFirstSyncDone] = useState(false);
  // Per-user, per-device onboarding dismissal. Skipping the wizard leaves the
  // ledger empty, so without this flag the gate below would re-show it every
  // render. Keyed by username so a different user on a shared browser still
  // gets their own first-run experience.
  const onboardingSkipKey = `hushold.onboarding.skipped:${auth.username ?? ''}`;
  const [wizardDismissed, setWizardDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(onboardingSkipKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    workerRef.current = new CryptoWorker() as Worker;
    return () => { workerRef.current?.terminate(); };
  }, []);

  // Cross-device language sync: when the encrypted `language` setting projects
  // from the event log (on unlock, and on any later synced change), the synced
  // value wins over the device-local one. '' means the user never explicitly
  // chose a language, so the device-detected locale stays.
  useEffect(() => {
    const synced = state.appSettings.language;
    if (synced && SUPPORTED_LOCALE_CODES.includes(synced) && synced !== i18n.language) {
      void i18n.changeLanguage(synced);
    }
  }, [state.appSettings.language, i18n]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    void (async () => {
      try {
        // Load the ENCRYPTED snapshot and decrypt it in the worker (SEC-05). No
        // plaintext is ever read from disk.
        const bytes = await loadEncryptedSnapshot();
        if (bytes && workerRef.current && auth.keyData) {
          const cached = await decryptSnapshotViaWorker(workerRef.current, bytes, auth.keyData);
          if (cached.length > 0) dispatch({ type: 'EVENTS_LOADED', events: cached });
        }
      } catch { /* IndexedDB unavailable, or stale snapshot from a prior key — ignore */ }

      if (auth.token && workerRef.current) {
        await doSync(workerRef.current);
      }
      // Cache + first sync have resolved; it is now safe to judge whether this
      // is a brand-new ledger and show the first-run wizard.
      setFirstSyncDone(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  // Persist the event log to IndexedDB as an ENCRYPTED snapshot whenever it
  // changes (SEC-05). Debounced to coalesce bursts (e.g. an initial sync merge).
  const snapshotTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const worker = workerRef.current;
    if (!auth.isAuthenticated || !worker || !auth.keyData || state.events.length === 0) return;
    if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
    snapshotTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const bytes = await encryptEventsViaWorker(worker, state.events, auth.keyData!);
          await saveEncryptedSnapshot(bytes);
        } catch { /* best-effort cache; the server remains the source of truth */ }
      })();
    }, 500);
    return () => {
      if (snapshotTimer.current) clearTimeout(snapshotTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.events, auth.isAuthenticated, auth.keyData]);

  // Phase 10 (offline): when connectivity returns, silently resume sync — no user
  // action required. Queued local writes were already persisted to the event log
  // and IndexedDB as they happened, so this just pulls anything new.
  const online = useOnlineStatus();
  const wasOnlineRef = useRef(online);
  useEffect(() => {
    if (online && !wasOnlineRef.current && auth.isAuthenticated && workerRef.current) {
      void doSync(workerRef.current);
    }
    wasOnlineRef.current = online;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, auth.isAuthenticated]);

  // Phase 10: encryptAndPush for the settings migration
  const encryptAndPush = useEncryptAndPush(workerRef.current, auth.keyData);
  // Automatic price sync: fires once per unlock when the user has opted in
  // (Settings → Data). Idempotent — skips symbols already priced today.
  usePriceSync(workerRef.current, { auto: true });
  const encryptAndPushRef = useRef(encryptAndPush);
  encryptAndPushRef.current = encryptAndPush;

  const migratedRef = useRef(false);
  useEffect(() => {
    // Run after events are loaded so lastSequence is correct
    if (!auth.isAuthenticated || migratedRef.current || state.events.length === 0) return;
    migratedRef.current = true;

    void (async () => {
      const legacyCostBasis = localStorage.getItem('investment.costBasisMethod');
      if (legacyCostBasis !== 'fifo' && legacyCostBasis !== 'average') return;
      try {
        const event = await buildEvent(
          'SettingUpdated',
          { key: 'costBasisMethod', value: legacyCostBasis },
          state.ledger.lastSequence + 1,
        );
        await encryptAndPushRef.current(event);
        localStorage.removeItem('investment.costBasisMethod');
      } catch {
        // Non-fatal — will retry next session
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, state.events.length]);

  async function doSync(worker: Worker) {
    if (!auth.token || syncing) return;
    setSyncing(true);
    dispatch({ type: 'SYNC_START' });

    try {
      const config = { baseUrl: auth.syncUrl, token: auth.token };
      const blobs = await fetchSyncLog(config, state.ledger.lastSequence);

      if (blobs.length > 0 && auth.keyData) {
        const events = await decryptBlobs(blobs, worker, auth.keyData);
        if (events.length > 0) {
          // The encrypted-snapshot effect persists the merged log; no plaintext cache.
          dispatch({ type: 'EVENTS_LOADED', events });
        }
      }

      dispatch({ type: 'SYNC_SUCCESS' });
    } catch (err) {
      dispatch({ type: 'SYNC_ERROR', message: err instanceof Error ? err.message : 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  }

  const keyData = auth.keyData;
  const worker = workerRef.current;

  // Small-screen sidebar drawer. Declared before the isMobileShell() early return
  // so hook order stays stable across both shells. It auto-closes on any
  // navigation (every nav action changes location.pathname) and on Escape.
  const location = useLocation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNavOpen(false);
      // Global search shortcut: ⌘K on macOS, Ctrl+K on Windows/Linux. Works from
      // anywhere (including form fields) so it behaves like a command palette.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        navigate('/search');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // First-run setup wizard, hosted at its own /setup URL in both desktop and
  // native shells. Two concerns are deliberately decoupled:
  //   • Auto-routing into /setup happens only on first run and only if the user
  //     hasn't skipped onboarding on this device (wizardDismissed).
  //   • /setup itself always renders the wizard while the ledger is empty,
  //     regardless of the skip flag — so the "Set up starter accounts" link on
  //     the empty accounts page can reopen it after a skip.
  const ledgerIsEmpty = firstSyncDone && state.ledger.accounts.size === 0;
  const accountsExist = firstSyncDone && state.ledger.accounts.size > 0;

  function handleSkipWizard() {
    try {
      localStorage.setItem(onboardingSkipKey, '1');
    } catch { /* storage may be unavailable (private mode, etc.) */ }
    setWizardDismissed(true);
    navigate('/accounts', { replace: true });
  }

  if (ledgerIsEmpty && location.pathname === '/setup') {
    return <FirstRunWizard worker={worker} keyData={keyData} onSkip={handleSkipWizard} />;
  }
  if (ledgerIsEmpty && !wizardDismissed && location.pathname !== '/setup') {
    return <Navigate to="/setup" replace />;
  }
  // Accounts exist — /setup has no purpose, bounce any stray visit into the app.
  if (accountsExist && location.pathname === '/setup') {
    return <Navigate to="/accounts" replace />;
  }

  // DECISION: inside the Capacitor native shell, render the touch-first mobile
  // app (bottom tabs, card register, quick entry) instead of the desktop
  // sidebar/register. Gated on Capacitor.isNativePlatform() (via isMobileShell),
  // NOT viewport width — a narrow desktop browser keeps the desktop layout. All
  // the data/sync/migration effects above run identically in both shells.
  if (isMobileShell()) {
    return <MobileApp worker={worker} keyData={keyData} />;
  }

  return (
    <div className="h-screen flex flex-col bg-bg">
      <TopNav worker={worker} keyData={keyData} onMenuClick={() => setNavOpen(true)} />
      <div className="flex flex-1 overflow-hidden">
        {navOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={() => setNavOpen(false)}
            aria-hidden
          />
        )}
        <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
        <main className="flex-1 flex overflow-hidden bg-bg main-content">
          {worker ? (
            <Routes>
              <Route path="/accounts" element={<AccountTree worker={worker} keyData={keyData} />} />
              <Route path="/accounts/:id" element={<TransactionRegister worker={worker} keyData={keyData} />} />
              <Route path="/search" element={<GlobalSearch />} />
              <Route path="/scheduled-transactions" element={<ScheduledTransactions worker={worker} keyData={keyData} />} />
              <Route path="/budgets" element={<BudgetManager worker={worker} keyData={keyData} />} />
              <Route path="/budgets/:id" element={<BudgetEditor worker={worker} keyData={keyData} />} />
              <Route path="/reports/transactions" element={<TransactionReport />} />
              <Route path="/reports/balance-sheet" element={<BalanceSheet />} />
              <Route path="/reports/income-statement" element={<IncomeStatement />} />
              <Route path="/reports/cash-flow" element={<CashFlow />} />
              <Route path="/reports/net-worth" element={<NetWorthChart />} />
              <Route path="/reports/expense-breakdown" element={<ExpenseBreakdown />} />
              <Route path="/reports/income-vs-expenses" element={<IncomeVsExpenses />} />
              <Route path="/reports/budget" element={<BudgetReport />} />
              <Route path="/import" element={<ImportPage worker={worker} keyData={keyData} />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/reports/capital-gains" element={<CapitalGainsReport />} />
              <Route path="/settings" element={<Settings worker={worker} />} />
              <Route path="/settings/commodities" element={<CommodityManager worker={worker} keyData={keyData} />} />
              <Route path="/settings/commodities/:commodityId/prices" element={<PriceEditor worker={worker} keyData={keyData} />} />
              <Route path="/settings/exchange-rates" element={<ExchangeRateManager worker={worker} keyData={keyData} />} />
              <Route path="/reports/currency-gain-loss" element={<CurrencyGainLoss />} />
              <Route index element={<Navigate to="/accounts" replace />} />
              <Route path="*" element={<Navigate to="/accounts" replace />} />
            </Routes>
          ) : (
            <div className="flex-1 flex items-center justify-center text-text-muted">
              Initializing…
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
